import moment from 'moment';
import { formatMoneyWithCurrency } from '../../../../../../utils/formatter';

const eventToAction = (event) => {
  const t = {
    CREATE: 'CREATED',
    CREATED: 'CREATED',
    EDIT: 'UPDATED',
    UPDATE: 'UPDATED',
    UPDATED: 'UPDATED',
    DELETE: 'DELETE',
    REVERSE: 'REVERSED',
    REVERSED: 'REVERSED',
  };
  return t[String(event).toUpperCase()] || 'UPDATED';
};

const parseJson = (value) => {
  if (value == null) return null;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  }
  return null;
};

const toLinesArray = (value) => {
  const parsed = parseJson(value);
  if (Array.isArray(parsed)) return parsed;
  if (Array.isArray(value)) return value;
  return [];
};

const formatEntryDate = (value) => {
  const parsed = parseJson(value);
  if (parsed?.fullDate) return moment(parsed.fullDate).format('DD/MM/YYYY');
  if (parsed?.day != null && parsed?.month != null && parsed?.year != null) {
    return `${String(parsed.day).padStart(2, '0')}/${String(parsed.month).padStart(2, '0')}/${parsed.year}`;
  }
  if (typeof value === 'string' && value) return value;
  return '—';
};

const formatStringDate = (value) => {
  if (!value) return '—';
  const iso = moment(value, ['YYYY-MM-DD', moment.ISO_8601], true);
  if (iso.isValid()) return iso.format('DD/MM/YYYY');
  return String(value);
};

const normalizeForCompare = (key, value) => {
  switch (key) {
    case 'entryDate':
      return formatEntryDate(value);
    case 'stringDate':
      return formatStringDate(value);
    case 'entries':
      return JSON.stringify(toLinesArray(value));
    case 'isHidden':
      return String(value === true || value === 'true');
    default:
      return String(value ?? '');
  }
};

const fieldHasChanged = (field) => {
  const { key, value, oldValue } = field;
  return normalizeForCompare(key, oldValue) !== normalizeForCompare(key, value);
};

const getFieldLabel = (key, t) => {
  const label = t(`entryHistory.fields.${key}`, { defaultValue: '' });
  return label || key;
};

const formatDisplayValue = (key, value) => {
  switch (key) {
    case 'entryDate':
      return formatEntryDate(value);
    case 'stringDate':
      return formatStringDate(value);
    default:
      if (value == null || value === '') return '—';
      return String(value);
  }
};

const formatAmount = (amount, currency) =>
  formatMoneyWithCurrency(Number(amount || 0), currency || 'CLP');

const labeledAccount = (line) => {
  const key = String(line?.accountKey || '').trim();
  const name = String(line?.accountName || '').trim();
  if (key && name) return `${key} ${name}`;
  return '';
};

const getLineLabel = (line, fallbackLine, index, t) => {
  const account = labeledAccount(line) || labeledAccount(fallbackLine);
  if (account) return account;

  const key = String(line?.accountKey || fallbackLine?.accountKey || '').trim();
  if (key) return key;

  return t('entryHistory.lineNumber', { number: index + 1 });
};

const normalizeCostCenters = (line) => {
  const list = Array.isArray(line?.costCenters) ? line.costCenters : [];
  return list
    .map((cc) => {
      const rawId = cc?.costCenterId;
      const id = String(rawId?.$oid || rawId || '').trim();
      return {
        id: id === 'undefined' ? '' : id,
        label: String(cc?.label || '').trim(),
        debit: Number(cc?.debit || 0),
        credit: Number(cc?.credit || 0),
      };
    })
    .filter((cc) => cc.id || cc.label)
    .sort((a, b) => `${a.label}|${a.id}`.localeCompare(`${b.label}|${b.id}`));
};

const costCenterSignature = (centers) =>
  centers.map((cc) => `${cc.id}|${cc.label}|${cc.debit}|${cc.credit}`).join(';');

const formatCostCenters = (centers, currency) => {
  if (!centers.length) return '—';
  return centers
    .map((cc) => {
      const amount = cc.debit > 0 ? cc.debit : cc.credit;
      const name = cc.label || cc.id || '—';
      return `${name} · ${formatAmount(amount, currency)}`;
    })
    .join(', ');
};

const formatLineAmountsWithT = (line, currency, t) => {
  const parts = [];
  if (Number(line?.debit || 0) > 0) {
    parts.push(`${t('entryHistory.lineFields.debitShort')} ${formatAmount(line.debit, currency)}`);
  }
  if (Number(line?.credit || 0) > 0) {
    parts.push(`${t('entryHistory.lineFields.creditShort')} ${formatAmount(line.credit, currency)}`);
  }
  return parts.join(' · ') || '—';
};

const compareEntryLines = (oldValue, newValue, t, currency) => {
  const oldLines = toLinesArray(oldValue);
  const newLines = toLinesArray(newValue);
  const lineDiffs = [];
  const maxLen = Math.max(oldLines.length, newLines.length);

  for (let i = 0; i < maxLen; i += 1) {
    const oldLine = oldLines[i];
    const newLine = newLines[i];

    if (!oldLine && newLine) {
      lineDiffs.push({
        kind: 'added',
        label: getLineLabel(newLine, null, i, t),
        summary: t('entryHistory.lineAdded', {
          amounts: formatLineAmountsWithT(newLine, currency, t),
        }),
      });
      continue;
    }

    if (oldLine && !newLine) {
      lineDiffs.push({
        kind: 'removed',
        label: getLineLabel(oldLine, null, i, t),
        summary: t('entryHistory.lineRemoved', {
          amounts: formatLineAmountsWithT(oldLine, currency, t),
        }),
      });
      continue;
    }

    const fields = [];
    const oldKey = String(oldLine.accountKey || '').trim();
    const newKey = String(newLine.accountKey || '').trim();
    const oldName = String(oldLine.accountName || '').trim();
    const newName = String(newLine.accountName || '').trim();
    const accountChanged = oldKey !== newKey || (oldName && newName && oldName !== newName);

    if (accountChanged) {
      fields.push({
        label: t('entryHistory.lineFields.account'),
        oldValue: `${oldKey} ${oldName}`.trim() || '—',
        newValue: `${newKey} ${newName}`.trim() || '—',
      });
    }

    const oldDebit = Number(oldLine.debit || 0);
    const newDebit = Number(newLine.debit || 0);
    const oldCredit = Number(oldLine.credit || 0);
    const newCredit = Number(newLine.credit || 0);
    const debitChanged = oldDebit !== newDebit;
    const creditChanged = oldCredit !== newCredit;
    const sideText = (debit, credit) => {
      if (debit > 0 && credit <= 0) {
        return `${t('entryHistory.lineFields.debitShort')} ${formatAmount(debit, currency)}`;
      }
      if (credit > 0 && debit <= 0) {
        return `${t('entryHistory.lineFields.creditShort')} ${formatAmount(credit, currency)}`;
      }
      return formatLineAmountsWithT({ debit, credit }, currency, t);
    };

    if (debitChanged && creditChanged) {
      fields.push({
        oldValue: sideText(oldDebit, oldCredit),
        newValue: sideText(newDebit, newCredit),
      });
    } else if (debitChanged) {
      fields.push({
        label: t('entryHistory.lineFields.debit'),
        oldValue: formatAmount(oldDebit, currency),
        newValue: formatAmount(newDebit, currency),
      });
    } else if (creditChanged) {
      fields.push({
        label: t('entryHistory.lineFields.credit'),
        oldValue: formatAmount(oldCredit, currency),
        newValue: formatAmount(newCredit, currency),
      });
    }
    if ((oldLine.rut || '') !== (newLine.rut || '')) {
      fields.push({
        label: t('entryHistory.lineFields.rut'),
        oldValue: oldLine.rut || '—',
        newValue: newLine.rut || '—',
      });
    }
    if ((oldLine.businessName || '') !== (newLine.businessName || '')) {
      fields.push({
        label: t('entryHistory.lineFields.businessName'),
        oldValue: oldLine.businessName || '—',
        newValue: newLine.businessName || '—',
      });
    }

    const oldCenters = normalizeCostCenters(oldLine);
    const newCenters = normalizeCostCenters(newLine);
    if (costCenterSignature(oldCenters) !== costCenterSignature(newCenters)) {
      fields.push({
        label: t('entryHistory.lineFields.costCenters'),
        oldValue: formatCostCenters(oldCenters, currency),
        newValue: formatCostCenters(newCenters, currency),
      });
    }

    if (fields.length > 0) {
      lineDiffs.push({
        kind: 'modified',
        label: getLineLabel(newLine, oldLine, i, t),
        fields,
      });
    }
  }

  return lineDiffs;
};

const buildEntriesChange = (oldValue, newValue, t, currency) => {
  const oldLines = toLinesArray(oldValue);
  const newLines = toLinesArray(newValue);
  const lineDiffs = compareEntryLines(oldValue, newValue, t, currency);

  let summary = null;
  if (oldLines.length !== newLines.length) {
    summary = t('entryHistory.linesCountChanged', {
      oldCount: oldLines.length,
      newCount: newLines.length,
    });
  }

  if (lineDiffs.length === 0) {
    return {
      key: 'entries',
      label: t('entryHistory.fields.entries'),
      type: 'summary',
      summary: summary || t('entryHistory.linesChanged', { count: newLines.length }),
    };
  }

  return {
    key: 'entries',
    label: t('entryHistory.fields.entries'),
    type: 'lineDiffs',
    summary,
    lineDiffs,
  };
};

const INTERNAL_FIELDS = new Set(['sourceEntryId', 'index', 'reverseCounterPartId', 'reversed']);

const buildFieldChange = (field, t, entry) => {
  const { key, value, oldValue } = field;

  if (INTERNAL_FIELDS.has(key)) return null;

  if (key === 'isHidden') {
    if (value === true || value === 'true') {
      return {
        key,
        label: getFieldLabel(key, t),
        type: 'summary',
        summary: t('entryHistory.deleted'),
      };
    }
    return null;
  }

  if (key === 'entries') {
    return buildEntriesChange(oldValue, value, t, entry?.currency);
  }

  return {
    key,
    label: getFieldLabel(key, t),
    type: 'diff',
    oldValue: formatDisplayValue(key, oldValue),
    newValue: formatDisplayValue(key, value),
  };
};

const hasCreateEvent = (editionHistory) =>
  (editionHistory || []).some((e) =>
    ['CREATE', 'CREATED'].includes(String(e.event).toUpperCase()),
  );

export const buildEntryHistoryItems = (entry, t) => {
  if (!entry) return [];

  const items = [];
  const editionHistory = entry.editionHistory || [];
  const entryId = entry.id || entry._id;

  if (entry.creationInformation && !hasCreateEvent(editionHistory)) {
    const ci = entry.creationInformation;
    const d = ci.creationDate ? moment(ci.creationDate) : moment();
    items.push({
      id: `entry-${entryId}-created`,
      userEmail: ci.userEmail || 'Sistema',
      action: 'CREATED',
      date: d.format('DD/MM/YYYY'),
      time: d.format('HH:mm'),
      summary: t('entryHistory.created'),
      changes: [],
      sortDate: d.toDate(),
    });
  }

  editionHistory.forEach((e, idx) => {
    const d = e.date ? moment(e.date) : moment();
    const action = e.event ? eventToAction(e.event) : 'UPDATED';

    let summary = null;
    let changes = [];

    if (action === 'DELETE') {
      summary = t('entryHistory.deleted');
    } else if (action === 'REVERSED') {
      summary = t('entryHistory.reversed');
    } else if (e.fields?.length) {
      changes = e.fields
        .filter(fieldHasChanged)
        .map((field) => buildFieldChange(field, t, entry))
        .filter(Boolean);

      if (changes.length === 0) {
        const allInternal = e.fields.every((f) => INTERNAL_FIELDS.has(f.key));
        if (allInternal) return;
        summary = t('entryHistory.updated');
      }
    } else {
      summary = t('entryHistory.updated');
    }

    items.push({
      id: `entry-${entryId}-edition-${idx}-${e.date || idx}`,
      userEmail: e.userEmail || e.userId || 'Usuario',
      action,
      date: d.format('DD/MM/YYYY'),
      time: d.format('HH:mm'),
      summary,
      changes,
      sortDate: d.toDate(),
    });
  });

  return items.sort((a, b) => {
    const dateA = a.sortDate || moment(a.date, 'DD/MM/YYYY').toDate();
    const dateB = b.sortDate || moment(b.date, 'DD/MM/YYYY').toDate();
    if (dateA.getTime() !== dateB.getTime()) return dateB - dateA;
    return (b.time || '').localeCompare(a.time || '');
  });
};

export const getEntryHistoryTitle = (entry, t) => {
  if (!entry) return t('entryHistory.title');
  const number = entry.entryNumber;
  if (entry.entryType === 'matchEntry') {
    return t('entryRow.matchTitle', { number });
  }
  if (entry.entryType === 'emissionEntry') {
    return t('entryRow.emissionTitle', { number });
  }
  return t('entryRow.freeTitle', { number });
};
